# API Reference

Function signatures and usage patterns. For complete type definitions, see [Types](/docs#types).

---

## defineFlow

Defines a flow with one or more steps. Each step is a logic statement: a match condition paired with a decision.

```ts
export const musicDiscoveryFlow = defineFlow("music_discovery_flow", (flow) => {
  flow.step(
    { toolName: { is: "get_recent_tracks" } },
    { nextTool: "get_recommendations" },
  );
  flow.step(
    { toolName: { is: "get_recommendations" } },
    { nextTool: "create_playlist" },
  );
});
```

### Signature

```ts
function defineFlow(
  flowId: string,
  builder: (flow: FlowBuilder) => void,
): FlowDefinition;
```

**Parameters:**

- `flowId`: Unique identifier for this flow
- `builder`: Function that receives a `FlowBuilder` to register steps

### flow.step()

```ts
flow.step(match: MatchCondition, input: StepInput): void
```

**Parameters:**

- `match`: A `MatchCondition` object describing the conditions under which this step applies. All fields are optional -- steps can match on any combination of `toolName`, `params`, and `result`. Each field uses an explicit operator object (`{ is: ... }` or `{ isNot: ... }`).
- `input`: A `StepDecision` object (creates a logic statement). For backwards compatibility, a `StepHandler` function is also accepted.

**Examples:**

```ts
// Match on toolName
flow.step({ toolName: { is: "scan_repo" } }, { nextTool: "configure_app" });

// Match on toolName with isNot
flow.step(
  { toolName: { isNot: "abort_session" } },
  { nextTool: "continue_flow" },
);

// Match on toolName and params
flow.step(
  { toolName: { is: "scan_repo" }, params: { language: { is: "python" } } },
  { nextTool: "configure_python" },
);

// Match on result only
flow.step(
  { result: { hasConfig: { is: true } } },
  { nextTool: "review_config" },
);

// Multiple decision fields
flow.step(
  { toolName: { is: "scan_repo" } },
  {
    nextTool: "configure_app",
    primaryAction: "configure_app",
    contentArea: "setup",
  },
);
```

> **Backwards compatibility:** Handler functions are also supported for dynamic decisions that require runtime logic beyond what match conditions express:
>
> ```ts
> flow.step({ toolName: { is: "scan_repo" } }, (ctx) => ({
>   nextTool: "configure_app",
> }));
> ```

See [FlowBuilder](/docs#types/flow-definition), [MatchCondition](/docs#types/logic-statement-types), and [FlowDefinition](/docs#types/flow-definition) for complete type details.

---

## createTaias

Creates a Taias instance from a flow definition and optional affordance registry.

```ts
export const taias = createTaias({
  flow: musicDiscoveryFlow,
  affordances: registry,
  devMode: true,
  onMissingStep: ({ toolName }) => {
    console.warn(`[Taias] No step defined for tool "${toolName}"`);
  },
});
```

### Signature

```ts
function createTaias<S extends string = DefaultSlots>(
  options: TaiasOptions<S>,
): Taias<S>;
```

**Type Parameter:**

- `S`: The slot type. Defaults to `DefaultSlots` (`"primaryCta" | "secondaryCta" | "widgetVariant"`). Pass a custom union type for custom slots.

**Options:**

- `flow`: Your flow definition (from `defineFlow`)
- `affordances`: Optional registry of UI affordances (from `mergeAffordances`)
- `devMode`: Enable validation and warnings (default: `false`)
- `debug`: Enable built-in debug output -- `true` for default format, or `{ format, logger }` for customization. See [Observability](/docs#observability).
- `tracing`: Trace level -- `"summary"` (default) or `"detailed"`. Controls depth of evaluation data captured. See [Observability](/docs#observability).
- `onMissingStep`: Callback when a tool has no defined step
- `onWarn`: Custom warning handler (defaults to `console.warn`)

**Example with custom slots:**

```ts
type MySlots = "primaryCta" | "contentArea" | "headerStyle";

// Each binding declares its key - decision fields are inferred automatically
const affordances = defineAffordances<MySlots>((r) => {
  r.primaryCta("cta", { key: "nextTool", value: "createUser" });
  r.contentArea("content", { key: "contentArea", value: "form" });
  r.headerStyle("header", { key: "headerStyle", value: "progress" });
});

const taias = createTaias<MySlots>({
  flow,
  affordances: mergeAffordances([affordances]),
});
```

See [TaiasOptions](/docs#types/instance-creation) for complete type details.

---

## Taias

The Taias instance provides methods for resolving affordances and subscribing to events. The instance is generic over slot type `S`.

### resolve()

Evaluates match conditions against the given context to find the applicable step, then produces advice, decision, and UI selections. Match conditions are evaluated using their operators (`is` for equality, `isNot` for negation) across all specified fields (`toolName`, `params`, `result`). Taias uses per-field indexing for efficient lookup: steps with `is` conditions are indexed at build time, and candidate sets are intersected at resolve time. Steps with only `isNot` conditions are evaluated as a fallback. You can pass optional `params` and `result` as context that match conditions can match against.

```ts
const affordances = await taias.resolve({
  toolName: "get_recent_tracks",
  params: { limit: 20 }, // Tool input parameters (optional)
  result: { trackCount: 15, hasMore: true }, // Tool execution output (optional)
});

// affordances.advice - LLM guidance text
// affordances.decision - { nextTool: "get_recommendations" }
// affordances.selections - { primaryCta: { handleId, bindsTo }, ... }
```

**Signature:**

```ts
resolve(ctx: TaiasContext): Affordances<S> | null | Promise<Affordances<S> | null>
```

**Parameters:**

- `ctx.toolName`: The tool name to resolve
- `ctx.params`: The input parameters of the tool call (optional)
- `ctx.result`: The output of the tool's execution (optional)

**Returns:**

- `{ advice, decision, selections }` if a matching step exists and produces a decision
- `null` if no step matches or the handler returns `null`

**Return shape:**

```ts
{
  advice: string;              // LLM guidance text
  decision: {                  // The flow decision (includes all fields from logic statement or handler)
    nextTool: string;
    [key: string]: string;     // Any custom fields
  };
  selections: {                // UI affordance selections (may be empty)
    // With default slots:
    primaryCta?: { handleId: string; bindsTo: { key: string; value: string } };
    secondaryCta?: { handleId: string; bindsTo: { key: string; value: string } };
    widgetVariant?: { handleId: string; bindsTo: { key: string; value: string } };
    // With custom slots, keys match your slot type
  };
}
```

See [Affordances](/docs#types/affordances) for complete type details.

### on() (Advanced)

Subscribe to events emitted by the Taias instance. For most use cases, the `debug` option on `createTaias` is simpler -- see [Observability](/docs#observability).

```ts
taias.on("resolve", (event) => {
  console.log(event.trace.matched, event.decision);
});
```

**Signature:**

```ts
on<E extends keyof TaiasEventMap<S>>(
  event: E,
  handler: (data: TaiasEventMap<S>[E]) => void,
): void
```

Currently the only event is `"resolve"`, which emits a `ResolveEvent<S>` after every `resolve()` call.

### off()

Unsubscribe a previously registered event handler.

```ts
taias.off("resolve", handler);
```

**Signature:**

```ts
off<E extends keyof TaiasEventMap<S>>(
  event: E,
  handler: (data: TaiasEventMap<S>[E]) => void,
): void
```

Pass the same function reference that was used with `on()`. If the handler was not registered, this is a no-op.

---

## createDebugSubscriber (Advanced)

Creates a handler function for subscribing to resolve events via the event emitter. Most developers should use the `debug` option on `createTaias` instead -- see [Observability](/docs#observability).

```ts
import { createDebugSubscriber } from "taias-sdk";

taias.on("resolve", createDebugSubscriber());
taias.on("resolve", createDebugSubscriber({ format: "compact" }));
taias.on("resolve", createDebugSubscriber({ logger: myLogger.debug }));
```

### Signature

```ts
function createDebugSubscriber(
  options?: DebugSubscriberOptions,
): (event: ResolveEvent) => void;
```

**Options:**

- `format`: `"default"` (multi-line breakdown) or `"compact"` (single line per resolve). Default: `"default"`.
- `logger`: Custom log function. Default: `console.log`.

See [Observability](/docs#observability) for output examples.

---

## defineAffordances

Defines UI affordances for a widget using a builder pattern.

```ts
export const trackListAffordances = defineAffordances((r) => {
  r.primaryCta("tracks.cta.recommend", { toolName: "get_recommendations" });
  r.primaryCta("tracks.cta.playlist", { toolName: "create_playlist" });
  r.widgetVariant("tracks.variant.discovery", {
    toolName: "get_recommendations",
  });
});
```

### Signature

```ts
function defineAffordances<S extends string = DefaultSlots>(
  builder: (r: AffordanceRegistrar<S>) => void,
): AffordanceRegistry<S>;
```

**Type Parameter:**

- `S`: The slot type. Defaults to `DefaultSlots`. The registrar will have methods for each slot in `S`.

**Builder methods:**

With default slots, you get:

- `r.primaryCta(handleId, bindsTo)`: Register a primary CTA option
- `r.secondaryCta(handleId, bindsTo)`: Register a secondary CTA option
- `r.widgetVariant(handleId, bindsTo)`: Register a widget variant option

With custom slots, the registrar has methods matching your slot names:

```ts
type MySlots = "primaryCta" | "contentArea" | "headerStyle";

defineAffordances<MySlots>((r) => {
  r.primaryCta("cta.x", { toolName: "x" }); // ✓ Valid
  r.contentArea("content.x", { toolName: "x" }); // ✓ Valid
  r.headerStyle("header.x", { toolName: "x" }); // ✓ Valid
  // r.secondaryCta("...", {...});               // ✗ Type error! Not in MySlots
});
```

**Binding formats:**

```ts
// Shorthand - binds to decision.nextTool
{ toolName: "get_recommendations" }

// Generalized form - binds to any decision field
{ key: "primaryAction", value: "startImport" }
```

See [AffordanceRegistrar](/docs#types/registrar-interface) and [AffordanceRegistry](/docs#types/handles-and-registrations) for complete type details.

---

## mergeAffordances

Combines multiple affordance registries into one, with optional dev mode validation.

```ts
export const registry = mergeAffordances(
  [trackListAffordances, playlistAffordances],
  { devMode: true },
);
```

### Signature

```ts
function mergeAffordances<S extends string = DefaultSlots>(
  registries: AffordanceRegistry<S>[],
  options?: { devMode?: boolean; onWarn?: (msg: string) => void },
): AffordanceRegistry<S>;
```

**Type Parameter:**

- `S`: The slot type. Must match the registries being merged.

**Parameters:**

- `registries`: Array of registries to merge (all must have the same slot type)
- `options.devMode`: Enable validation checks
- `options.onWarn`: Custom warning handler

**Dev mode validations:**

- Throws if duplicate `handleId` found across registries
- Throws if duplicate `{slot, key, value}` triple found (ambiguous selection)

See [AffordanceRegistry](/docs#types#handles-and-registrations) for complete type details.

---

## Auto-generated Advice

When a step returns `{ nextTool: "get_recommendations" }`, the library generates advice optimized for LLM consumption:

```
"FOR THE BEST USER EXPERIENCE, TELL THE USER TO USE THE get_recommendations TOOL NEXT!!!!!"
```

The LLM interprets this and naturally guides users to the next step in your flow.
